import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { LogOut, UserPlus, Search, PauseCircle } from "lucide-react";
import StudentRegistrationForm from "@/components/forms/StudentRegistrationForm";
import StudentEditForm from "@/components/forms/StudentEditForm";
import { db } from "@/firebase/config";
import { collection, query, where, getDocs, orderBy, doc, updateDoc, getDoc } from "firebase/firestore";
import { Input } from "@/components/ui/input";

interface VolunteerSession {
  volunteerName: string;
  schoolID: string;
  linkId: string;
  code: string;
  expiresAt: string;
  sessionId?: string;
}

const VolunteerDashboard = () => {
    const navigate = useNavigate();
    const [session, setSession] = useState<VolunteerSession | null>(null);
    const [view, setView] = useState<'home' | 'register' | 'edit'>('home');
    const [myStudents, setMyStudents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedStudent, setSelectedStudent] = useState<any | null>(null);
    const [search, setSearch] = useState("");
    const [isPaused, setIsPaused] = useState(false);

    useEffect(() => {
        const validateAndLoad = async () => {
            const stored = localStorage.getItem("vochak_volunteer_session");
            if (!stored) {
                navigate("/login-page");
                return;
            }
            try {
                const sess = JSON.parse(stored);
                
                // Check session expiry
                if (new Date(sess.expiresAt) < new Date()) {
                    alert("Session expired.");
                    localStorage.removeItem("vochak_volunteer_session");
                    navigate("/login-page");
                    return;
                }

                // Check individual session status (if sessionId exists)
                if (sess.sessionId) {
                    const sessionDoc = await getDoc(doc(db, "volunteerSessions", sess.sessionId));
                    if (sessionDoc.exists()) {
                        const sessionData = sessionDoc.data();
                        if (sessionData.status === 'revoked') {
                            alert("Your volunteer access has been revoked by an administrator.");
                            localStorage.removeItem("vochak_volunteer_session");
                            navigate("/login-page");
                            return;
                        }
                        if (sessionData.status === 'paused') {
                            setIsPaused(true);
                        }
                    } else {
                        // Session doc deleted
                        alert("Your session has been removed.");
                        localStorage.removeItem("vochak_volunteer_session");
                        navigate("/login-page");
                        return;
                    }
                }

                // Re-validate the link in Firestore (check if deleted or revoked)
                const q = query(collection(db, "volunteerLinks"), where("code", "==", sess.code));
                const snapshot = await getDocs(q);
                
                if (snapshot.empty) {
                    alert("Your access link has been removed by an administrator.");
                    localStorage.removeItem("vochak_volunteer_session");
                    navigate("/login-page");
                    return;
                }

                const linkData = snapshot.docs[0].data();
                
                if (linkData.status === 'revoked') {
                    alert("Your access has been revoked by an administrator.");
                    localStorage.removeItem("vochak_volunteer_session");
                    navigate("/login-page");
                    return;
                }

                if (new Date(linkData.expiresAt) < new Date()) {
                    alert("This invitation link has expired.");
                    localStorage.removeItem("vochak_volunteer_session");
                    navigate("/login-page");
                    return;
                }

                // All checks passed
                setSession(sess);
                setLoading(false);
                fetchMyEntries(sess);
            } catch (e) {
                console.error("Session validation error:", e);
                navigate("/login-page");
            }
        };

        validateAndLoad();
    }, []);

    const fetchMyEntries = async (sess: VolunteerSession) => {
        setLoading(true);
        try {
            // Fetch students submitted by this volunteer
            // submittedBy format is "Volunteer: Name" (as set in RegistrationForm)
            const createdByString = `Volunteer: ${sess.volunteerName}`;
            
            const q = query(
                collection(db, `schools/${sess.schoolID}/students`),
                where("submittedBy", "==", createdByString),
                orderBy("createdAt", "desc")
            );

            const snapshot = await getDocs(q);
            const students = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMyStudents(students);
        } catch (e) {
            console.error("Error fetching entries:", e);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        if (confirm("Are you sure you want to end your session?")) {
            localStorage.removeItem("vochak_volunteer_session");
            navigate("/login-page");
        }
    };

    const handleEdit = (student: any) => {
        setSelectedStudent(student);
        setView('edit');
    };

    const handleStudentUpdated = async (updatedData: any) => {
         // Perform the update to Firestore
         if (!session || !selectedStudent) return;
         
         const studentRef = doc(db, `schools/${session.schoolID}/students`, selectedStudent.id);
         try {
             await updateDoc(studentRef, updatedData);
             alert("Student updated successfully.");
             setSelectedStudent(null);
             setView('home');
             fetchMyEntries(session);
         } catch (e) {
             console.error(e);
             alert("Failed to update student.");
         }
    };

    if (!session) return null;

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-orange-600 border-t-transparent mx-auto"></div>
                    <p className="mt-4 text-gray-500">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    // Paused state - show message but allow viewing entries
    if (isPaused) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-10">
                <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
                    <div className="max-w-6xl mx-auto flex justify-between items-center">
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Volunteer Portal</h1>
                        <Button variant="outline" onClick={handleLogout}>
                            <LogOut className="h-4 w-4 mr-2" /> End Session
                        </Button>
                    </div>
                </header>
                <main className="max-w-2xl mx-auto p-6 mt-10">
                    <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
                        <CardContent className="pt-6 text-center">
                            <PauseCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-amber-900 dark:text-amber-100 mb-2">
                                Access Paused
                            </h2>
                            <p className="text-amber-700 dark:text-amber-300 mb-6">
                                Your volunteer access has been temporarily paused by an administrator.
                                You can still view your registered students but cannot register new ones.
                            </p>
                            <p className="text-sm text-amber-600 dark:text-amber-400">
                                If you believe this is an error, please contact the school administrator.
                            </p>
                        </CardContent>
                    </Card>
                </main>
            </div>
        );
    }

    if (view === 'register') {
        return (
            <div className="bg-gray-100 min-h-screen">
                <StudentRegistrationForm 
                    onBack={() => {
                        setView('home');
                        fetchMyEntries(session);
                    }}
                    isVolunteer={true}
                    volunteerSchoolID={session.schoolID}
                    volunteerName={session.volunteerName}
                    sessionId={session.sessionId}
                />
            </div>
        );
    }

    if (view === 'edit' && selectedStudent) {
        return (
             <div className="bg-gray-100 min-h-screen p-6">
                <StudentEditForm
                    schoolID={session.schoolID}
                    student={selectedStudent}
                    onBack={() => setView('home')}
                    onSave={handleStudentUpdated}
                    isVolunteer={true}
                    volunteerName={session.volunteerName}
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-10">
            {/* Header */}
            <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 sticky top-0 z-10 shadow-sm">
                <div className="max-w-6xl mx-auto flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                            Vochak Volunteer Portal
                        </h1>
                        <p className="text-sm text-gray-500">Welcome, {session.volunteerName}</p>
                    </div>
                    <Button variant="outline" onClick={handleLogout} className="text-red-600 border-red-200 hover:bg-red-50">
                        <LogOut className="h-4 w-4 mr-2" /> End Session
                    </Button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
                
                {/* Actions Card */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                     <Card className="md:col-span-1 bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800/50">
                        <CardHeader>
                            <CardTitle>Quick Actions</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Button className="w-full h-12 text-lg bg-orange-600 hover:bg-orange-700 text-white shadow-lg" onClick={() => setView('register')}>
                                <UserPlus className="h-5 w-5 mr-2" />
                                Register New Student
                            </Button>
                        </CardContent>
                     </Card>

                     <Card className="md:col-span-2">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <div>
                                <CardTitle>My Recent Entries</CardTitle>
                                <CardDescription>Students you have registered this session</CardDescription>
                            </div>
                            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                Count: {myStudents.length}
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="mb-4">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                                    <Input 
                                        placeholder="Search your entries..." 
                                        className="pl-9"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                {loading && <div className="text-center py-4">Loading entries...</div>}
                                {!loading && myStudents.length === 0 && (
                                    <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                                        No students registered yet.
                                    </div>
                                )}
                                {myStudents
                                    .filter(s => 
                                        search === "" || 
                                        s.firstName?.toLowerCase().includes(search.toLowerCase()) || 
                                        s.lastName?.toLowerCase().includes(search.toLowerCase()) ||
                                        s.registrationNumber?.toLowerCase().includes(search.toLowerCase())
                                    )
                                    .map(student => (
                                    <div key={student.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border rounded-lg hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 font-bold">
                                                {student.firstName?.[0]}
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900 dark:text-white">
                                                    {student.firstName} {student.lastName}
                                                </h4>
                                                <p className="text-xs text-gray-500 font-mono">
                                                    {student.registrationNumber} • {student.admissionGrade}
                                                </p>
                                            </div>
                                        </div>
                                        <Button variant="ghost" size="sm" onClick={() => handleEdit(student)}>
                                            Edit
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                     </Card>
                </div>
            </main>
        </div>
    );
};

export default VolunteerDashboard;
